import React, {
  memo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import MuiTooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import {
  grayColor,
  dangerColor,
  blendColors,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react";

import { ThreeDots } from "react-loader-spinner";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Button from "components/CustomButtons/Button";
import ModalHeader from "components/ModalHeader";
import Pnl from "components/Pnl/Pnl";
import { PnlSharedProvider } from "hooks/usePnlShared";
import useThemeColors from "hooks/useThemeColors";

import alignmentSessionStyles from "./styles";
import modalStyle from "assets/jss/material-dashboard-pro-react/modalStyle";
import HeatmapTable from "./HeatmapTable";
import FocusModeWrapper from "./FocusModeWrapper";
import { usePnlConfig } from "./hooks";
import { mockMessages } from "./constants";
import {
  createSessionMessage,
  updateSessionMessage,
  deleteSessionMessage,
} from "services/governanceService";
import { getPnlTitleFromPeriods, getMonthAbbr } from "./utils";

// Merge alignment session styles with modal styles
const useStyles = makeStyles((theme) => ({
  ...alignmentSessionStyles,
  ...modalStyle(theme),
}));

// Gray color for previous year bars
const PREVIOUS_YEAR_COLOR = "rgba(0, 0, 0, 0.25)";
// Darker color for legend text (to maintain readability)
const LEGEND_TEXT_COLOR = "rgba(0, 0, 0, 0.65)";

// Message limits
const MAX_MESSAGES = 5;
const MAX_TITLE_LENGTH = 40;
const MAX_CONTENT_LENGTH = 300;

// Helper to extract plain text from HTML
const getPlainTextFromHtml = (html) => {
  if (!html) return "";
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

// Simple text editor with bold, italic and underline options
// Uses key prop from parent to reset when modal opens
const SimpleTextEditor = memo(function SimpleTextEditor({
  initialContent,
  onContentChange,
}) {
  // Initialize editor state only once on mount
  const [editorState, setEditorState] = useState(() => {
    if (initialContent) {
      const blocksFromHtml = htmlToDraft(initialContent);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });

  // Store onContentChange in ref to avoid recreating handler
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;

  const handleEditorStateChange = useCallback((newState) => {
    setEditorState(newState);
    onContentChangeRef.current(
      draftToHtml(convertToRaw(newState.getCurrentContent())),
    );
  }, []);

  return (
    <Editor
      editorState={editorState}
      onEditorStateChange={handleEditorStateChange}
      wrapperStyle={{
        border: `1px solid ${grayColor[8]}`,
        borderRadius: 4,
      }}
      toolbarStyle={{
        border: "none",
        borderBottom: `1px solid ${grayColor[8]}`,
        marginBottom: 0,
        padding: "4px 8px",
      }}
      editorStyle={{
        fontSize: 14,
        lineHeight: 1.5,
        minHeight: 120,
        padding: "8px 12px",
        backgroundColor: grayColor[12],
      }}
      toolbar={{
        options: ["inline"],
        inline: {
          options: ["bold", "italic", "underline"],
        },
      }}
    />
  );
});

SimpleTextEditor.propTypes = {
  initialContent: PropTypes.string,
  onContentChange: PropTypes.func.isRequired,
};

// Custom tooltip for grouped bar charts
function GroupedBarTooltip({
  active,
  payload,
  label,
  unit,
  decimals,
  clientColor,
  currentLabel,
  previousLabel,
}) {
  if (active && payload && payload.length) {
    const currentValue = payload.find((p) => p.dataKey.includes("Current"));
    const previousValue = payload.find((p) => p.dataKey.includes("Previous"));
    return (
      <div
        style={{
          backgroundColor: "rgba(255,255,255,0.95)",
          border: "1px solid #ccc",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "12px",
        }}
      >
        <div style={{ fontWeight: 500, marginBottom: "6px" }}>{label}</div>
        {currentValue && (
          <div style={{ color: clientColor, marginBottom: "2px" }}>
            {currentLabel}:{" "}
            {currentValue.value.toLocaleString("pt-BR", {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })}{" "}
            {unit}
          </div>
        )}
        {previousValue && (
          <div style={{ color: grayColor[6] }}>
            {previousLabel}:{" "}
            {previousValue.value.toLocaleString("pt-BR", {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })}{" "}
            {unit}
          </div>
        )}
      </div>
    );
  }
  return null;
}

GroupedBarTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  label: PropTypes.string,
  unit: PropTypes.string,
  decimals: PropTypes.number,
  clientColor: PropTypes.string,
  currentLabel: PropTypes.string,
  previousLabel: PropTypes.string,
};

/**
 * Calculate order of magnitude for chart axis formatting
 * Returns { divisor, suffix, decimals }
 */
const calculateChartOom = (maxValue) => {
  if (maxValue >= 1e9) return { divisor: 1e9, suffix: "B", decimals: 1 };
  if (maxValue >= 1e6) return { divisor: 1e6, suffix: "M", decimals: 1 };
  if (maxValue >= 1e3) return { divisor: 1e3, suffix: "k", decimals: 1 };
  return { divisor: 1, suffix: "", decimals: 0 };
};

/**
 * Memoized Charts component to prevent re-renders from P&L mouse events
 */
const OverviewCharts = memo(function OverviewCharts({
  clientColor,
  t,
  chartData,
  heatmapData,
  currentYear,
  previousYear,
  volumeUnit,
  focusMode = false,
}) {
  const classes = useStyles();

  // Calculate OOM for volume and revenue based on max values (ignoring nulls)
  const volumeOom = useMemo(() => {
    if (!chartData || chartData.length === 0)
      return { divisor: 1e6, suffix: "M", decimals: 1 };
    const volumes = chartData
      .flatMap((d) => [d.volumeCurrent, d.volumePrevious])
      .filter((v) => v != null);
    if (volumes.length === 0) return { divisor: 1e6, suffix: "M", decimals: 1 };
    const maxVolume = Math.max(...volumes);
    return calculateChartOom(maxVolume);
  }, [chartData]);

  const revenueOom = useMemo(() => {
    if (!chartData || chartData.length === 0)
      return { divisor: 1e6, suffix: "M", decimals: 1 };
    const revenues = chartData
      .flatMap((d) => [d.revenueCurrent, d.revenuePrevious])
      .filter((v) => v != null);
    if (revenues.length === 0)
      return { divisor: 1e6, suffix: "M", decimals: 1 };
    const maxRevenue = Math.max(...revenues);
    return calculateChartOom(maxRevenue);
  }, [chartData]);

  // Format axis tick with OOM
  const formatVolumeTick = useCallback(
    (value) => {
      const scaled = value / volumeOom.divisor;
      if (volumeOom.decimals === 0) {
        return `${Math.round(scaled)}${volumeOom.suffix}`;
      }
      return `${scaled.toFixed(volumeOom.decimals)}${volumeOom.suffix}`;
    },
    [volumeOom],
  );

  const formatRevenueTick = useCallback(
    (value) => {
      const scaled = value / revenueOom.divisor;
      if (revenueOom.decimals === 0) {
        return `${Math.round(scaled)}${revenueOom.suffix}`;
      }
      return `${scaled.toFixed(revenueOom.decimals)}${revenueOom.suffix}`;
    },
    [revenueOom],
  );

  // Volume chart title with unit
  const volumeChartTitle = useMemo(() => {
    const baseTitle = t("alignment_session.overview.chart_volume");
    return volumeUnit ? `${baseTitle} (${volumeUnit})` : baseTitle;
  }, [t, volumeUnit]);

  return (
    <Card
      className={classes.overviewCard}
      style={focusMode ? { minHeight: "unset", height: "100%" } : {}}
    >
      <CardBody className={classes.overviewCardBody}>
        <div className={classes.chartsStack}>
          {/* Chart 1: Volume - Year over Year comparison */}
          <div className={classes.chartBox}>
            <div className={classes.chartTitle}>{volumeChartTitle}</div>
            <div className={classes.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  barGap={0}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={formatVolumeTick}
                    width={45}
                  />
                  <Tooltip
                    content={
                      <GroupedBarTooltip
                        unit={volumeUnit || ""}
                        decimals={0}
                        clientColor={clientColor}
                        currentLabel={String(currentYear)}
                        previousLabel={String(previousYear)}
                      />
                    }
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{ fontSize: 10, paddingBottom: 4 }}
                    formatter={(value) => (
                      <span style={{ color: LEGEND_TEXT_COLOR }}>{value}</span>
                    )}
                    payload={[
                      {
                        value: String(previousYear),
                        type: "square",
                        color: PREVIOUS_YEAR_COLOR,
                      },
                      {
                        value: String(currentYear),
                        type: "square",
                        color: clientColor,
                      },
                    ]}
                  />
                  <Bar
                    dataKey="volumePrevious"
                    name={String(previousYear)}
                    fill={PREVIOUS_YEAR_COLOR}
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar
                    dataKey="volumeCurrent"
                    name={String(currentYear)}
                    fill={clientColor}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Revenue - Year over Year comparison */}
          <div className={classes.chartBox}>
            <div className={classes.chartTitle}>
              {t("alignment_session.overview.chart_revenue")}
            </div>
            <div className={classes.chartWrapper}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
                  barGap={0}
                  barCategoryGap="20%"
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={formatRevenueTick}
                    width={45}
                  />
                  <Tooltip
                    content={
                      <GroupedBarTooltip
                        unit={revenueOom.suffix}
                        decimals={revenueOom.decimals}
                        clientColor={clientColor}
                        currentLabel={String(currentYear)}
                        previousLabel={String(previousYear)}
                      />
                    }
                  />
                  <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{ fontSize: 10, paddingBottom: 4 }}
                    formatter={(value) => (
                      <span style={{ color: LEGEND_TEXT_COLOR }}>{value}</span>
                    )}
                    payload={[
                      {
                        value: String(previousYear),
                        type: "square",
                        color: PREVIOUS_YEAR_COLOR,
                      },
                      {
                        value: String(currentYear),
                        type: "square",
                        color: clientColor,
                      },
                    ]}
                  />
                  <Bar
                    dataKey="revenuePrevious"
                    name={String(previousYear)}
                    fill={PREVIOUS_YEAR_COLOR}
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar
                    dataKey="revenueCurrent"
                    name={String(currentYear)}
                    fill={clientColor}
                    radius={[2, 2, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 3: Heatmap Month x Year */}
          <div className={classes.chartBox}>
            <div className={classes.chartTitle}>
              {t("alignment_session.overview.chart_heatmap")}
            </div>
            <div className={classes.heatmapWrapper}>
              <HeatmapTable heatmapData={heatmapData} />
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
});

OverviewCharts.propTypes = {
  clientColor: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  chartData: PropTypes.array,
  heatmapData: PropTypes.object,
  currentYear: PropTypes.number,
  previousYear: PropTypes.number,
  volumeUnit: PropTypes.string,
  focusMode: PropTypes.bool,
};

/**
 * OverviewSection component
 * Displays the overview page with P&L, charts, and messages
 */
function OverviewSection({
  clientColor,
  activeSession,
  pnlMetadata,
  dataOrchestrator,
  onSessionUpdate,
  focusMode = false,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { clientTextColor } = useThemeColors();

  // Check if page data is still loading
  const isPageLoading = dataOrchestrator?.isPageLoading("overview") || false;

  // Generate P&L title from the current comparison periods
  const pnlTitle = useMemo(() => {
    const periods = dataOrchestrator?.getComparisonPeriods?.();
    return getPnlTitleFromPeriods(periods?.current, t);
  }, [dataOrchestrator, t]);

  // Get P&L values from orchestrator cache (already prefetched)
  const pnlValues = dataOrchestrator?.getCachedPnlData?.();

  // True while the selected period's data is still being fetched
  // (e.g. user switched from YTD to L12M but L12M hasn't arrived yet).
  // Shows inline ThreeDots spinners inside each P&L value cell.
  const isPnlRefreshing = !pnlValues && !!activeSession;

  // Use loaded values or empty object (P&L will show "-" for missing values)
  const effectivePnlValues = pnlValues || {};

  // Get overview data from orchestrator
  const overviewData = dataOrchestrator?.overviewData;

  // Process overview data for bar charts (year over year comparison)
  const { chartData, heatmapData, currentYear, previousYear } = useMemo(() => {
    if (!overviewData || overviewData.length === 0 || !activeSession) {
      return {
        chartData: [],
        heatmapData: { years: [], months: [], values: {} },
        currentYear: 0,
        previousYear: 0,
      };
    }

    const refYear = activeSession.year;
    const refMonth = activeSession.period_value;
    const prevYear = refYear - 1;

    // Build chart data for bar charts (all 12 months, comparing current vs previous year)
    const chartDataArr = [];
    for (let m = 1; m <= 12; m++) {
      const currentData = overviewData.find(
        (d) => d.year === refYear && d.month === m,
      );
      const previousData = overviewData.find(
        (d) => d.year === prevYear && d.month === m,
      );

      // Show data only for months that have data (up to reference month for current year)
      const hasCurrentData = m <= refMonth && currentData?.volume != null;
      const hasPreviousData = previousData?.volume != null;

      chartDataArr.push({
        month: getMonthAbbr(m - 1, t),
        volumeCurrent: hasCurrentData ? currentData.volume : null,
        volumePrevious: hasPreviousData ? previousData.volume : null,
        revenueCurrent: hasCurrentData ? currentData.net_revenue : null,
        revenuePrevious: hasPreviousData ? previousData.net_revenue : null,
      });
    }

    // Build heatmap data (4 years: reference year + 3 previous years)
    // Format expected by HeatmapTable: { years: [], months: [], values: { year: [val1, val2, ...] } }
    const yearsForHeatmap = [refYear - 3, refYear - 2, refYear - 1, refYear];
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const heatmapValues = {};

    yearsForHeatmap.forEach((year) => {
      heatmapValues[year] = months.map((m) => {
        // Skip future months for current year
        if (year === refYear && m > refMonth) return null;

        const monthData = overviewData.find(
          (d) => d.year === year && d.month === m,
        );
        if (monthData && monthData.net_revenue && monthData.net_revenue !== 0) {
          // Calculate serve margin percentage
          return (monthData.serve_margin / monthData.net_revenue) * 100;
        }
        return null;
      });
    });

    return {
      chartData: chartDataArr,
      heatmapData: {
        years: yearsForHeatmap,
        months,
        values: heatmapValues,
      },
      currentYear: refYear,
      previousYear: prevYear,
    };
  }, [overviewData, activeSession, t]);

  // Transform session messages to local format
  const sessionMessages = useMemo(() => {
    if (!activeSession?.messages) return [];
    return activeSession.messages
      .filter((m) => m.page_key === "overview")
      .sort((a, b) => a.order - b.order)
      .map((m) => ({
        id: m.id,
        title: m.title,
        text: m.content,
        type: m.message_type,
      }));
  }, [activeSession]);

  // Local state for messages - use session messages if available, else mock
  const [messages, setMessages] = useState(mockMessages);
  const [savingMessage, setSavingMessage] = useState(false);

  // Update messages when session changes
  useEffect(() => {
    if (activeSession) {
      setMessages(sessionMessages);
    } else {
      setMessages(mockMessages);
    }
  }, [activeSession, sessionMessages]);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editOrder, setEditOrder] = useState(0);
  const [editorKey, setEditorKey] = useState(0);

  // Ref for auto-focusing the title input
  const titleInputRef = useRef(null);

  // Handle opening edit modal
  const handleEditMessage = useCallback(
    (index) => {
      setEditingIndex(index);
      setEditTitle(messages[index].title);
      setEditContent(messages[index].text);
      setEditOrder(index);
      setEditorKey((prev) => prev + 1);
      setEditModalOpen(true);
    },
    [messages],
  );

  // Handle opening add modal
  const handleAddMessage = useCallback(() => {
    setEditingIndex(null);
    setEditTitle("");
    setEditContent("");
    setEditOrder(messages.length);
    setEditorKey((prev) => prev + 1);
    setEditModalOpen(true);
  }, [messages.length]);

  // Handle saving message
  const handleSaveMessage = useCallback(async () => {
    if (!activeSession?.id) {
      // No session - just update local state (mock mode)
      if (editingIndex !== null) {
        const currentOrder = editingIndex;
        const newOrder = editOrder;

        setMessages((prev) => {
          const updated = prev.map((msg, idx) =>
            idx === currentOrder
              ? { ...msg, title: editTitle, text: editContent }
              : msg,
          );

          if (currentOrder !== newOrder) {
            const [item] = updated.splice(currentOrder, 1);
            updated.splice(newOrder, 0, item);
          }
          return updated;
        });
      } else {
        setMessages((prev) => {
          const newMsg = { title: editTitle, text: editContent };
          const updated = [...prev];
          updated.splice(editOrder, 0, newMsg);
          return updated;
        });
      }
      setEditModalOpen(false);
      return;
    }

    // Has active session - use API
    setSavingMessage(true);
    try {
      if (editingIndex !== null) {
        // Edit existing message
        const messageId = messages[editingIndex]?.id;
        if (messageId) {
          await updateSessionMessage(activeSession.id, messageId, {
            title: editTitle,
            content: editContent,
            order: editOrder,
          });
        }
      } else {
        // Create new message
        await createSessionMessage(activeSession.id, {
          page_key: "overview",
          title: editTitle,
          content: editContent,
          message_type: "period_result",
          order: editOrder,
        });
      }
      // Refresh session data to get updated messages
      if (onSessionUpdate) {
        await onSessionUpdate();
      }
      setEditModalOpen(false);
    } catch (error) {
      console.error("Failed to save message:", error);
    } finally {
      setSavingMessage(false);
    }
  }, [
    activeSession?.id,
    editingIndex,
    editTitle,
    editContent,
    editOrder,
    messages,
    onSessionUpdate,
  ]);

  // Handle close modal
  const handleCloseModal = useCallback(() => {
    setEditModalOpen(false);
  }, []);

  // Handle delete message
  const handleDeleteMessage = useCallback(async () => {
    if (editingIndex === null) return;

    if (!activeSession?.id) {
      // No session - just update local state (mock mode)
      setMessages((prev) => prev.filter((_, idx) => idx !== editingIndex));
      setEditModalOpen(false);
      return;
    }

    // Has active session - use API
    const messageId = messages[editingIndex]?.id;
    if (!messageId) {
      setEditModalOpen(false);
      return;
    }

    setSavingMessage(true);
    try {
      await deleteSessionMessage(activeSession.id, messageId);
      // Refresh session data to get updated messages
      if (onSessionUpdate) {
        await onSessionUpdate();
      }
      setEditModalOpen(false);
    } catch (error) {
      console.error("Failed to delete message:", error);
    } finally {
      setSavingMessage(false);
    }
  }, [activeSession?.id, editingIndex, messages, onSessionUpdate]);

  // Stable callback for editor content change
  const handleContentChange = useCallback((content) => {
    setEditContent(content);
  }, []);

  // Count plain text characters in content
  const contentCharCount = useMemo(() => {
    return getPlainTextFromHtml(editContent).length;
  }, [editContent]);

  // Check if max messages reached
  const maxMessagesReached = messages.length >= MAX_MESSAGES;

  const {
    plStructure,
    netRevenueAnchor,
    serveMarginAnchor,
    volumeUnit,
    volumeL12M,
    oom,
    staticPnlProps,
    drillDownsOpen,
    setDrillDownsOpen,
    mouseOverPLLine,
    mouseOverPLLineFade,
    timersAllowed,
    handleMouseOverPLLine,
    handleMouseLeavePLLine,
  } = usePnlConfig({
    translateStructure: true,
    pnlMetadata,
    plValues: effectivePnlValues,
  });

  return (
    <FocusModeWrapper focusMode={focusMode}>
      <div
        className={classes.overviewContainer}
        style={focusMode ? { height: "100%", marginTop: 0 } : {}}
      >
        {/* Card 1 - Messages (Comentários da Gestão) */}
        <Card
          className={classes.overviewCard}
          style={focusMode ? { minHeight: "unset", height: "100%" } : {}}
        >
          <div
            className={classes.sectionTitleCardHeader}
            style={{
              borderBottom:
                "1px solid " + blendColors(clientColor, whiteColor, 0.25),
            }}
          >
            {t("alignment_session.overview.messages_title")}
          </div>
          <CardBody
            className={classes.overviewCardBody}
            style={{ paddingTop: "6px" }}
          >
            <div className={classes.messagesContainer}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`${classes.messageCard} ${isPageLoading ? classes.messageCardLoading : ""}`}
                  style={{ borderLeftColor: clientColor, position: "relative" }}
                >
                  <IconButton
                    size="small"
                    className={classes.messageEditButton}
                    onClick={() => handleEditMessage(index)}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </IconButton>
                  <div className={classes.messageTitle}>{msg.title}</div>
                  <div
                    className={classes.messageText}
                    dangerouslySetInnerHTML={{ __html: msg.text }}
                  />
                </div>
              ))}

              {/* Add button */}
              <Button
                color="transparent"
                className={classes.messageAddButton}
                onClick={handleAddMessage}
                disabled={maxMessagesReached}
                style={
                  maxMessagesReached
                    ? { opacity: 0.5, cursor: "not-allowed" }
                    : {}
                }
              >
                <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
                {t("alignment_session.overview.add_message")}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Edit/Add Message Modal */}
        <Dialog
          classes={{
            root: classes.modalRoot,
            paper: classes.modal,
          }}
          open={editModalOpen}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
        >
          <ModalHeader
            title={
              editingIndex !== null
                ? t("alignment_session.overview.edit_message")
                : t("alignment_session.overview.add_message")
            }
            onClose={handleCloseModal}
            closeDisabled={savingMessage}
            closeLabel={t("common.close")}
            clientColor={clientColor}
          />
          <DialogContent
            className={classes.modalBody}
            style={{ paddingLeft: "16px", paddingRight: "16px" }}
          >
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {t("alignment_session.overview.message_title_label")}
                </label>
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  autoFocus
                  maxLength={MAX_TITLE_LENGTH}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${grayColor[8]}`,
                    borderRadius: 4,
                    fontSize: 14,
                  }}
                />
              </div>
              <div style={{ width: 100 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 4,
                    fontWeight: 500,
                    fontSize: 13,
                  }}
                >
                  {t("alignment_session.overview.message_order_label") ||
                    "Posição"}
                </label>
                <select
                  value={editOrder}
                  onChange={(e) => setEditOrder(parseInt(e.target.value, 10))}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: `1px solid ${grayColor[8]}`,
                    borderRadius: 4,
                    fontSize: 14,
                    backgroundColor: whiteColor,
                  }}
                >
                  {Array.from({
                    length:
                      editingIndex !== null
                        ? messages.length
                        : messages.length + 1,
                  }).map((_, i) => (
                    <option key={i} value={i}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 4,
                  fontWeight: 500,
                  fontSize: 13,
                }}
              >
                {t("alignment_session.overview.message_content_label")}
              </label>
              <SimpleTextEditor
                key={editorKey}
                initialContent={editContent}
                onContentChange={handleContentChange}
              />
              <div
                style={{
                  textAlign: "right",
                  fontSize: 11,
                  color:
                    contentCharCount > MAX_CONTENT_LENGTH
                      ? dangerColor[0]
                      : grayColor[4],
                  marginTop: 4,
                }}
              >
                {contentCharCount}/{MAX_CONTENT_LENGTH}
              </div>
            </div>
          </DialogContent>
          <DialogActions
            className={classes.modalFooter}
            style={{ justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {editingIndex !== null && (
                <MuiTooltip title={t("common.delete")}>
                  <IconButton
                    onClick={handleDeleteMessage}
                    disabled={savingMessage}
                    style={{
                      color: dangerColor[0],
                      padding: 8,
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </MuiTooltip>
              )}
              {savingMessage && (
                <div style={{ marginLeft: "8px" }}>
                  <ThreeDots height={20} width={30} color={clientColor} />
                </div>
              )}
            </div>
            <div>
              <Button
                color="transparent"
                onClick={handleCloseModal}
                disabled={savingMessage}
              >
                {t("common.cancel")}
              </Button>
              <Button
                style={{
                  backgroundColor: clientColor,
                  color: clientTextColor,
                }}
                onClick={handleSaveMessage}
                disabled={
                  !editTitle.trim() ||
                  savingMessage ||
                  contentCharCount > MAX_CONTENT_LENGTH
                }
              >
                {t("common.save")}
              </Button>
            </div>
          </DialogActions>
        </Dialog>

        {/* Card 2 - P&L (single component with name, value, percentage) */}
        <Card
          className={classes.overviewCard}
          style={focusMode ? { minHeight: "unset", height: "100%" } : {}}
        >
          <CardBody className={classes.overviewCardBodyPnl}>
            <PnlSharedProvider
              plValues={effectivePnlValues}
              plStructure={plStructure}
              benchmark={null}
              netRevenueAnchor={netRevenueAnchor}
              serveMarginAnchor={serveMarginAnchor}
              oom={oom}
              t={t}
            >
              <Pnl
                plStructure={plStructure}
                netRevenueAnchor={netRevenueAnchor}
                serveMarginAnchor={serveMarginAnchor}
                numPL={1}
                numPLs={1}
                plName={pnlTitle}
                handleMouseLeavePLLine={handleMouseLeavePLLine}
                handleMouseOverPLLine={handleMouseOverPLLine}
                mouseOverPLLine={mouseOverPLLine}
                mouseOverPLLineFade={mouseOverPLLineFade}
                timersAllowed={timersAllowed.current}
                drillDownsOpen={drillDownsOpen}
                setDrillDownsOpen={setDrillDownsOpen}
                plValues={effectivePnlValues}
                volumeUnit={volumeUnit}
                volumeL12M={volumeL12M}
                oom={oom}
                showColumns={["name", "val", "val_volume", "percentage"]}
                {...staticPnlProps}
                isRefreshing={isPnlRefreshing}
                plsToRefresh={isPnlRefreshing ? [1] : []}
              />
            </PnlSharedProvider>
          </CardBody>
        </Card>

        {/* Card 3 - Charts (memoized to prevent re-renders from P&L mouse events) */}
        <OverviewCharts
          clientColor={clientColor}
          t={t}
          chartData={chartData}
          heatmapData={heatmapData}
          currentYear={currentYear}
          previousYear={previousYear}
          volumeUnit={volumeUnit?.current}
          focusMode={focusMode}
        />
      </div>
    </FocusModeWrapper>
  );
}

OverviewSection.propTypes = {
  clientColor: PropTypes.string.isRequired,
  activeSession: PropTypes.object,
  pnlMetadata: PropTypes.object,
  dataOrchestrator: PropTypes.object,
  onSessionUpdate: PropTypes.func,
  focusMode: PropTypes.bool,
};

OverviewSection.defaultProps = {
  activeSession: null,
  pnlMetadata: null,
  dataOrchestrator: null,
  onSessionUpdate: null,
  focusMode: false,
};

export default OverviewSection;
